/**
 * Per-USER local preferences (as opposed to the per-business document in
 * utils/businessPrefs). One localStorage key, versioned, wiped on every auth
 * exit by services/Api/authRedirectHandler — so a merchant who signs out never
 * leaks their choices into the next login on the same browser.
 *
 * Keep this module dependency-free (localStorage only): it is imported by the
 * auth redirect handler, which sits below the API layer.
 */

const KEY = 'okc_user_prefs'
const VERSION = 1

export interface IUserPrefs {
  v: number
  // Premium ("Pro") app icon — utils/proIcon
  proIcon: {
    // The merchant switched the icon back to default themselves
    defaultOpted?: boolean
    // Expiry (epoch seconds) of the subscription the icon was last granted for;
    // a different expiry (renewal) resets the opt-out, like Android
    grantedExpiry?: number | null
  }
}

type Listener = () => void
const listeners = new Set<Listener>()
const notify = () => listeners.forEach((listener) => listener())

export const subscribeUserPrefs = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const defaults = (): IUserPrefs => ({ v: VERSION, proIcon: {} })

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

export const getUserPrefs = (): IUserPrefs => {
  try {
    const raw = storage()?.getItem(KEY)
    if (!raw) {
      return defaults()
    }
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.v !== VERSION) {
      return defaults()
    }
    return { v: VERSION, proIcon: { ...(parsed.proIcon || {}) } }
  } catch {
    return defaults()
  }
}

export const updateUserPrefs = (patch: { proIcon?: Partial<IUserPrefs['proIcon']> }): void => {
  const current = getUserPrefs()
  const next: IUserPrefs = { v: VERSION, proIcon: { ...current.proIcon, ...patch.proIcon } }
  if (JSON.stringify(next) === JSON.stringify(current)) {
    return
  }
  try {
    storage()?.setItem(KEY, JSON.stringify(next))
  } catch {}
  notify()
}

// Sign-out / session end: forget everything the user chose
export const clearUserPrefs = (): void => {
  try {
    storage()?.removeItem(KEY)
  } catch {}
  notify()
}

// Another tab changed the document
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === KEY || event.key === null) {
      notify()
    }
  })
}
